<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Drawer from 'primevue/drawer';
import { EventBusEvents, useEventBus } from '@apparatix/composables';
import { CrmDrawerDataReadyEvent } from './types';
import { objectNamePluralMap } from '@apparatix/schemas/Relationship';
import { SectionIconography } from './SectionIconography';
import { Typography } from '@apparatix/library/core';
import TimesIcon from '@primeicons/vue/times';
import AngleRightIcon from '@primeicons/vue/angle-right';
import ArrowLeftIcon from '@primeicons/vue/arrow-left';

const router = useRouter();
const route = useRoute();

const { listen } = useEventBus();

listen<CrmDrawerDataReadyEvent>(EventBusEvents.CrmDetailLoaded, data => {
  detailData.value = data;
});

const isVisible = ref(false);
const detailData = ref<CrmDrawerDataReadyEvent>();

const breadcrumbs = computed<Array<{ key: string, value: string }>>(() => detailData.value ? ['Sales', objectNamePluralMap[detailData.value.type], detailData.value.title].map(item => ({ key: crypto.randomUUID(), value: item })) : []);

const onDrawerHide = () => {
  if (route?.meta?.basePath) {
    // The timeout here is to allow for the drawer close animation to complete
    setTimeout(() => router.replace({ path: route.meta.basePath as string }), 250);
  }
};



onMounted(() => isVisible.value = true);
</script>
<template>
  <Drawer
    v-model:visible="isVisible"
    position="right"
    class="crm-drawer-container"
    :pt="{
      root: {
        class: 'border-0 overscroll-none'
      },
      mask: {
        class: 'overscroll-none crm-drawer-mask'
      }
    }"
    modal
    @hide="onDrawerHide"
  >
    <template #container>
      <div class="flex flex-col w-full h-full overflow-y-auto bg-[var(--white)]">
        <div v-if="detailData" class="flex flex-col w-full bg-[var(--white)]">
          <div class="w-full h-[35px] shrink-0 flex items-center border-b border-[var(--p-content-border-color)]">
            <div class="grow-0 flex items-center justify-center w-[35px] h-full border-r border-[var(--p-content-border-color)] cursor-pointer" @click="isVisible = false">
              <TimesIcon aria-label="Close drawer" />
            </div>
            <div class="grow flex gap-1 items-center h-full pl-3" :style="{backgroundColor: SectionIconography[detailData.type].backgroundColor}">
              <template v-for="(item, idx) in breadcrumbs" :key="item.key">
                <Typography variant="breadcrumb" :truncate="idx === breadcrumbs.length - 1">
                  {{ item.value }}
                </Typography>
                <AngleRightIcon v-if="idx < breadcrumbs.length - 1" />
              </template>
            </div>
            <div class="grow-0 h-full w-[35px] flex items-center justify-center bg-[var(--white)] border-l border-[var(--p-content-border-color)] cursor-pointer" @click="router.back">
              <ArrowLeftIcon aria-label="Back" />
            </div>
          </div>
        </div>
        <RouterView />
      </div>
    </template>
  </Drawer>
</template>
